import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Button,
  ButtonToolbar,
  Checkbox,
  FlexboxGrid,
  Form,
  SelectPicker,
  CheckPicker,
} from "rsuite";

import "./ProductFilters.css";
import { updateIn } from "json-immut";
import { HelperServices } from "../../../core/services/HelperServices";
import { ProductServices } from "../../../core/services/ProductServices";
import { get } from "lodash/fp";
import { ProductListParams } from "./ProductDataTypes";
import {
  statusObjArr,
  foodTypeObjArr,
  availableOnDayObjArr,
  sizeObjArr,
  operatorsObjArr,
  productNatureObjArr,
  productGenderObjArr,
} from "../../../core/config/AppConstants";

import { isNotEmpty } from "../../../utils/helpers";
import { useTranslation } from "react-i18next";

export interface PropTypesInterface {
  onSubmit: Function;
  onClear: Function;
}

export default function ProductFilters({
  onSubmit,
  onClear,
}: PropTypesInterface) {
  const { t } = useTranslation();
  const initSearchFormData = {
    product_type: "",
    product_category: "",
    product_name: "",
    product_sku: "",
    original_price: "",
    tentative_price: "",
    min_qty: "",
    hygenic_index: "",
    process_time: "",
    food_type: "",
    size: "",
    available_on_day: "",
    product_cost_percent: "",
    product_cost_percent_op: "gte",
    product_for_gender: "",
    product_nature: "",
    status: "",
  };
  const [formData, setFormData] =
    useState<ProductListParams>(initSearchFormData);
  const [productTypeList, setProductTypeList] = useState([]);
  const [categoryList, setCategoryList] = useState([]);
  const [showFilter, setShowFilter] = useState(false);
  const [disableCategory, setDisableCategory] = useState(true);
  const handleSearchToggle = () => setShowFilter(!showFilter);
  const countryList = useSelector((state: any) => state.country.data);

  function handleFormChanges(data: any) {
    let form = updateIn(formData, [data.path], data.value);
    setFormData(form);
  }

  async function init() {
    try {
      const productTypes = await ProductServices.getProductTypes();
      setProductTypeList(productTypes?.data?.data);
    } catch (error) {
      console.log(error);
    }
  }

  useEffect(() => {
    init();
  }, []);

  async function handleProductTypeChange(productTypeId: number) {
    try {
      const categoryRes = await ProductServices.getProductCategories(
        productTypeId
      );
      setCategoryList(get("data.data", categoryRes));
      setDisableCategory(false);
    } catch (error) {
      console.log(error);
    }
  }

  function clearSearch() {
    resetSelectionFor("product_type");
    handleFormChanges({
      path: "available_on_day",
      value: "",
    });
    onClear();
    setFormData(initSearchFormData);
  }

  function resetSelectionFor(resetFor: string) {
    let resetData: Object = {};
    switch (resetFor) {
      case "product_type":
        resetData = {
          product_type: "",
          product_category: "",
        };
        setCategoryList([]);
        setDisableCategory(true);
        break;
      case "category":
        resetData = {
          product_category: "",
        };
        break;
    }
    let newFormData = {
      ...formData,
      ...resetData,
    };
    setFormData(newFormData);
  }

  return (
    <div>
      <div className="row mb-2">
        <ButtonToolbar>
          <Button onClick={handleSearchToggle}>{t("ADVANCE_SEARCH")}</Button>
        </ButtonToolbar>
        {/* <div className="col-12">
          <hr className="mt-2 mb-2" />
        </div> */}
      </div>
      {showFilter && (
        <div className="lbl-blk customers content mb-3 pb-0">
          <Form
            formValue={formData}
            onChange={(data: any) => {
              let form = { ...formData, ...data };
              setFormData(form);
            }}
            layout="inline"
          >
            <FlexboxGrid justify="space-between">
              <FlexboxGrid.Item>
                <Form.Group controlId="product_type">
                  <Form.ControlLabel>
                    {t("PRODUCT")} {t("TYPE")}
                  </Form.ControlLabel>
                  <SelectPicker
                    data={productTypeList}
                    valueKey="id"
                    labelKey="product_type_name"
                    onSelect={(value, item) => {
                      handleFormChanges({ path: "product_type", value });
                      handleProductTypeChange(value);
                    }}
                    onClean={(e: any) => {
                      resetSelectionFor("product_type");
                    }}
                    name="product_type"
                    value={formData?.product_type}
                  />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="product_category">
                  <Form.ControlLabel>{t("CATEGORY")}</Form.ControlLabel>
                  <SelectPicker
                    data={categoryList}
                    valueKey="id"
                    labelKey="cat_name"
                    onSelect={(value, item) => {
                      handleFormChanges({ path: "product_category", value });
                    }}
                    onClean={(e: any) => {
                      resetSelectionFor("category");
                    }}
                    name="product_category"
                    value={formData?.product_category}
                    disabled={disableCategory}
                  />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="product_name">
                  <Form.ControlLabel>
                    {t("PRODUCT")} {t("NAME")}
                  </Form.ControlLabel>
                  <Form.Control name="product_name" />
                </Form.Group>
              </FlexboxGrid.Item>
            </FlexboxGrid>
            <FlexboxGrid justify="space-between">
              <FlexboxGrid.Item>
                <Form.Group controlId="product_cost_percent">
                  <Form.ControlLabel>
                    {t("PRODUCT")} {t("COST")} {t("%")}
                  </Form.ControlLabel>
                  <SelectPicker
                    data={operatorsObjArr}
                    valueKey="value"
                    labelKey="label"
                    onSelect={(value, item) => {
                      handleFormChanges({
                        path: "product_cost_percent_op",
                        value,
                      });
                    }}
                    onClean={(e: any) => {
                      handleFormChanges({
                        path: "product_cost_percent_op",
                        value: "",
                      });
                    }}
                    name="product_cost_percent_op"
                    value={formData.product_cost_percent_op}
                    defaultValue={operatorsObjArr[0].value}
                  />
                  <Form.Control name="product_cost_percent" />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="product_nature">
                  <Form.ControlLabel>
                    {t("PRODUCT")} {t("NATURE")}
                  </Form.ControlLabel>
                  <SelectPicker
                    data={productNatureObjArr}
                    valueKey="value"
                    labelKey="label"
                    onSelect={(value, item) => {
                      handleFormChanges({ path: "product_nature", value });
                    }}
                    onClean={(e: any) => {
                      handleFormChanges({ path: "product_nature", value: "" });
                    }}
                    name="product_nature"
                    value={formData?.product_nature}
                  />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="product_for_gender">
                  <Form.ControlLabel>
                    {t("FOR")} {t("GENDER")}
                  </Form.ControlLabel>
                  <SelectPicker
                    data={productGenderObjArr}
                    valueKey="value"
                    labelKey="label"
                    onSelect={(value, item) => {
                      handleFormChanges({ path: "product_for_gender", value });
                    }}
                    onClean={(e: any) => {
                      handleFormChanges({
                        path: "product_for_gender",
                        value: "",
                      });
                    }}
                    name="product_for_gender"
                    value={formData?.product_for_gender}
                  />
                </Form.Group>
              </FlexboxGrid.Item>
            </FlexboxGrid>
            <FlexboxGrid justify="space-between">
              <FlexboxGrid.Item>
                <Form.Group controlId="product_sku">
                  <Form.ControlLabel>
                    {t("PRODUCT")} {t("SKU")}
                  </Form.ControlLabel>
                  <Form.Control name="product_sku" />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="original_price">
                  <Form.ControlLabel>
                    {t("ORIGINAL")} {t("PRICE")}
                  </Form.ControlLabel>
                  <Form.Control name="original_price" />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="tentative_price">
                  <Form.ControlLabel>{t("PRICE")}</Form.ControlLabel>
                  <Form.Control name="tentative_price" />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="min_qty">
                  <Form.ControlLabel>
                    {t("MINIMUM")} {t("QUANTITY")}
                  </Form.ControlLabel>
                  <Form.Control name="min_qty" />
                </Form.Group>
              </FlexboxGrid.Item>

              <FlexboxGrid.Item>
                <Form.Group controlId="hygenic_index">
                  <Form.ControlLabel>
                    {t("HYGENIC")} {t("INDEX")}
                  </Form.ControlLabel>
                  <Form.Control name="hygenic_index" />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="process_time">
                  <Form.ControlLabel>
                    {t("PROCESS")} {t("TIME")}
                  </Form.ControlLabel>
                  <Form.Control name="process_time" />
                </Form.Group>
              </FlexboxGrid.Item>

              <FlexboxGrid.Item>
                <Form.Group controlId="food_type">
                  <Form.ControlLabel>
                    {t("FOOD")} {t("TYPE")}
                  </Form.ControlLabel>
                  <SelectPicker
                    data={foodTypeObjArr}
                    valueKey="value"
                    labelKey="label"
                    onSelect={(value, item) => {
                      handleFormChanges({ path: "food_type", value });
                    }}
                    onClean={(e: any) => {
                      handleFormChanges({ path: "food_type", value: "" });
                    }}
                    name="food_type"
                    value={formData?.food_type}
                  />
                </Form.Group>
              </FlexboxGrid.Item>
            </FlexboxGrid>
            <FlexboxGrid justify="space-between">
              <FlexboxGrid.Item>
                <Form.Group controlId="available_on_day">
                  <Form.ControlLabel>
                    {t("AVAILABLE")} {t("ON")}
                  </Form.ControlLabel>
                  <CheckPicker
                    sticky
                    onChange={(e: any) => {
                      let days: any = e.filter(isNotEmpty);
                      if (e.length) {
                        handleFormChanges({
                          path: "available_on_day",
                          value: days.toString(),
                        });
                      } else {
                        handleFormChanges({
                          path: "available_on_day",
                          value: "",
                        });
                      }
                    }}
                    onClean={() => {
                      handleFormChanges({
                        path: "available_on_day",
                        value: "",
                      });
                    }}
                    data={availableOnDayObjArr}
                    value={formData?.available_on_day?.split(",")}
                  />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="status">
                  <Form.ControlLabel>{t("STATUS")}</Form.ControlLabel>
                  <SelectPicker
                    data={statusObjArr}
                    valueKey="value"
                    labelKey="label"
                    onSelect={(value, item) => {
                      handleFormChanges({ path: "status", value });
                    }}
                    onClean={(e: any) => {
                      handleFormChanges({ path: "status", value: "" });
                    }}
                    name="status"
                    value={formData?.status}
                  />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="size">
                  <Form.ControlLabel>{t("SIZE")}</Form.ControlLabel>
                  <SelectPicker
                    data={sizeObjArr}
                    valueKey="value"
                    labelKey="label"
                    onSelect={(value, item) => {
                      handleFormChanges({ path: "size", value });
                    }}
                    onClean={(e: any) => {
                      handleFormChanges({ path: "size", value: "" });
                    }}
                    name="size"
                    value={formData?.size}
                  />
                </Form.Group>
              </FlexboxGrid.Item>
            </FlexboxGrid>
            <div className="filter-btn">
              <Button onClick={() => clearSearch()}>{t("CLEAR_FILTER")}</Button>
              <Button onClick={() => onSubmit(formData)}>{t("SEARCH")}</Button>
            </div>
          </Form>
        </div>
      )}
    </div>
  );
}
